/**
 * Cascade card on the Home page (P2.6.f).
 *
 * Lives in twin/ because it calls useCascade -- shared/layout/Home.tsx
 * isn't allowed to import twin/ (P0.8 module boundary), so the
 * composition root (App.tsx) lifts this component up and hands it to
 * Home via the bannerSlot prop. Same shape as the AppShell.headerSlot
 * seam used by PositionQueueWidget.
 *
 * Three visual states:
 *   - loading: small placeholder
 *   - error  : hidden entirely. The Home page is the operator's
 *              landing surface; a transient 401 or 5xx on cascade
 *              shouldn't shout at them on every refresh.
 *   - success: tile reads cascade_ibs_count + impacted_count with a
 *              tone matched to whether the bank is currently
 *              degraded (red) or clear (emerald), plus a click-
 *              through link to /twin/cascade.
 */
import { Link } from "react-router-dom";
import { Arrow } from "shared/components/Arrow";
import { useTranslation } from "react-i18next";

import { CARD_HOVER, CardError, cardClasses } from "shared/components/Card";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { StatusPill } from "shared/components/StatusPill";
import { useCascade } from "twin/api/hooks";

export function CascadeHomeCard() {
  const { t } = useTranslation();
  const cascade = useCascade();

  if (cascade.isError) {
    // P125.2 (brand P2.10): a calm visible "load failed" state instead of
    // silent-hiding -- an admin can tell "no data" from "load failed".
    return (
      <CardError
        testid="cascade-home-card-error"
        micro={t("modules.twin.cascade.homeCard.heading")}
        icon="cascade"
        message={t("modules.twin.cascade.homeCard.error")}
      />
    );
  }

  if (cascade.isLoading || !cascade.data) {
    return (
      <section
        data-testid="cascade-home-card-loading"
        className={cardClasses(null, "h-full flex flex-col text-sm text-slate-500")}
      >
        {t("modules.twin.cascade.homeCard.loading")}
      </section>
    );
  }

  const data = cascade.data;
  const degraded = data.cascade_ibs_count > 0 || data.impacted_count > 0;
  const notAssessed = !degraded && data.estate_assessed === false;
  // P124.1 (brand P1.4): RAG-as-marker -- a calm white card with a 3px
  // inline-start edge (red = degraded / green = clear) + a pill, never a
  // full fill. Hover stays brass (interaction affordance).
  return (
    <Link
      to="/twin/cascade"
      data-testid="cascade-home-card"
      data-degraded={degraded ? "true" : "false"}
      data-assessed={notAssessed ? "false" : "true"}
      className={cardClasses(
        /* Round 12 B1 -- a green status edge over a bank with nothing
           in it. The edge IS a verdict; an unassessed estate has not
           earned one in either direction. */
        degraded ? "red" : notAssessed ? null : "green",
        `${CARD_HOVER} block h-full flex flex-col`,
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <p className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
          <MarsadIcon name="cascade" size={13} className="text-brand-brassDark" />
          {t("modules.twin.cascade.homeCard.heading")}
        </p>
        <span className="text-[12.5px] font-semibold text-brand-brassDark">
          {t("modules.twin.cascade.homeCard.link")} <Arrow />
        </span>
      </header>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        {degraded ? (
          <>
            {/* P214.6 (Observatory): serif "bignum" headline -- the
                impacted count is the card's hero figure, in rag.red on
                the daylight card. */}
            <span
              data-testid="cascade-home-card-headline"
              className="font-display text-[34px] font-semibold leading-none tabular-nums text-rag-red"
            >
              {t("modules.twin.cascade.homeCard.ibsesImpacted", {
                count: data.ibs_impacted_count,
              })}
            </span>
            <StatusPill tone="red">
              {t("modules.twin.cascade.homeCard.openSummary", {
                count: data.impacted_count,
              })}
            </StatusPill>
          </>
        ) : (
          <>
            <span
              data-testid="cascade-home-card-headline"
              className="font-display text-2xl font-semibold leading-none text-ink"
            >
              {t(
                notAssessed
                  ? "modules.twin.cascade.homeCard.notAssessed"
                  : "modules.twin.cascade.homeCard.allClear",
              )}
            </span>
            <StatusPill tone={notAssessed ? "neutral" : "green"}>
              {t(
                notAssessed
                  ? "modules.twin.cascade.homeCard.notAssessedPill"
                  : "modules.twin.cascade.homeCard.clearPill",
              )}
            </StatusPill>
          </>
        )}
      </div>
    </Link>
  );
}
